import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabase';

/**
 * Hook to fetch cumulative TVL for all Ink pools from tokens_tvl table
 * This uses the pre-calculated TVL values from the cron job
 */
export const useCumulativePoolTVL = () => {
  const [cumulativeTVL, setCumulativeTVL] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [poolCount, setPoolCount] = useState<number>(0);

  useEffect(() => {
    const fetchCumulativeTVL = async () => {
      try {
        setLoading(true);
        setError(null);

        // Sum all TVL values for Ink network pools
        const { data, error: queryError } = await supabase
          .from('tokens_tvl')
          .select('tvl')
          .eq('network', 'ink');

        if (queryError) {
          throw new Error(`Failed to fetch cumulative TVL: ${queryError.message}`);
        }

        if (!data || data.length === 0) {
          setCumulativeTVL(0);
          setPoolCount(0);
          setLoading(false);
          return;
        }

        // Sum all TVL values
        const total = data.reduce((sum, row) => {
          const tvl = parseFloat(row.tvl as string) || 0;
          return sum + tvl;
        }, 0);

        setCumulativeTVL(total);
        setPoolCount(data.length);
        setLoading(false);
      } catch (err) {
        console.error('[useCumulativePoolTVL] Error:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch cumulative TVL');
        setLoading(false);
      }
    };

    fetchCumulativeTVL();

    // Refresh when page comes back into focus (user switches tabs and returns)
    // Since cron job updates every 24 hours, no need for frequent polling
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchCumulativeTVL();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return {
    cumulativeTVL,
    poolCount,
    loading,
    error,
  };
};

